import { DateLabelFormat } from '@unistocks/components/calendar/types';
import { createDate } from '@unistocks/shared/utils/util';
import { WeekdayNumbers } from 'luxon';
import type { ReactNode } from 'react';

interface GetWeekdaysNamesInput {
  /** Luxon locale used to format the weekday labels. */
  locale: string;
  /** Luxon format string, or a custom renderer. @default 'dd' */
  format?: DateLabelFormat;
  /** First weekday of the row. @default 1 — Monday */
  firstDayOfWeek?: WeekdayNumbers;
}

/**
 * Produces the 7 weekday labels for a calendar header, starting at
 * `firstDayOfWeek`. A string `format` is passed to Luxon; a function `format`
 * receives each day as `yyyy-MM-dd` and returns custom content. Mirrors
 * @mantine/dates `getWeekdayNames`.
 */
export function getWeekdayNames({
  locale,
  format = 'ccc',
  firstDayOfWeek = 1
}: GetWeekdaysNamesInput): ReactNode[] {
  const baseDate = createDate().set({ weekday: firstDayOfWeek });
  const labels: ReactNode[] = [];

  for (let i = 0; i < 7; i += 1) {
    const day = baseDate.plus({ days: i });

    if (typeof format === 'string') {
      labels.push(day.setLocale(locale).toFormat(format));
    } else {
      labels.push(format(day.toISODate()!));
    }
  }

  return labels;
}
